// Utils: imagePreloader
// Purpose: Preload critical images and fonts with caching, theme-aware
// selection, and progress tracking hooks for improved perceived performance.
/**
 * Utility for preloading images and fonts
 */

// Image cache to store preloaded images for instant access
const imageCache = new Map();

/**
 * Preload a single image with caching
 * @param {string} src - Image source URL
 * @returns {Promise<string>} - Promise that resolves with the image src
 */
export function preloadImage(src) {
  // Return cached image if already loaded
  if (imageCache.has(src)) {
    return Promise.resolve(src);
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      // Cache the loaded image
      imageCache.set(src, img);
      resolve(src);
    };
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });
}

/**
 * Check if an image is already cached
 * @param {string} src - Image source URL
 * @returns {boolean} - Whether the image is cached
 */
export function isImageCached(src) {
  return imageCache.has(src);
}

/**
 * Get cached image element
 * @param {string} src - Image source URL
 * @returns {HTMLImageElement|null} - Cached image element or null
 */
export function getCachedImage(src) {
  return imageCache.get(src) || null;
}

/**
 * Preload fonts using the Font Loading API
 * @returns {Promise<void>} - Promise that resolves when fonts are ready
 */
export function preloadFonts() {
  return new Promise((resolve) => {
    if (!document.fonts) {
      console.warn('Font Loading API not supported, skipping font preload');
      resolve();
      return;
    }
    
    console.log('Starting font preload...');
    
    // Wait for fonts to be ready
    document.fonts.ready.then(() => {
      console.log('Fonts are ready');
      resolve();
    }).catch((error) => {
      console.warn('Font loading failed:', error);
      resolve(); // Continue anyway
    });
    
    // Also try to load specific Adobe fonts if available
    if (window.Typekit) {
      try {
        window.Typekit.load({
          async: true,
          active: () => {
            console.log('Adobe fonts loaded successfully');
          },
          inactive: () => {
            console.warn('Adobe fonts failed to load');
          }
        });
      } catch (error) {
        console.warn('Error loading Adobe fonts:', error);
      }
    }
  });
}

/**
 * Preload multiple images with progress tracking
 * @param {string[]} imageSources - Array of image source URLs
 * @param {boolean} failSilently - Whether to continue if some images fail to load
 * @param {function} onProgress - Optional progress callback
 * @returns {Promise<string[]>} - Promise that resolves with array of loaded image sources
 */
export function preloadImages(imageSources, failSilently = true, onProgress = null) {
  let loadedCount = 0;
  
  const imagePromises = imageSources.map(src => {
    if (failSilently) {
      return preloadImage(src).then(result => {
        loadedCount++;
        if (onProgress) {
          onProgress(loadedCount, imageSources.length);
        }
        return result;
      }).catch(error => {
        console.warn(`Failed to preload image: ${src}`, error);
        loadedCount++;
        if (onProgress) {
          onProgress(loadedCount, imageSources.length);
        }
        return src; // Return src anyway to not block loading
      });
    } else {
      return preloadImage(src).then(result => {
        loadedCount++;
        if (onProgress) {
          onProgress(loadedCount, imageSources.length);
        }
        return result;
      });
    }
  });
  
  return Promise.all(imagePromises);
}

/**
 * Get critical images based on current theme to avoid loading unnecessary images
 */
export function getCriticalImagesByTheme() {
  const isDarkMode = document.documentElement.classList.contains('dark');
  
  return [
    // Logo images
    '/images/logos/logo-big-colored.png',
    '/images/logos/logo-big-white.png',
    '/images/logos/logo-color.png',
    '/images/logos/logo-white.png',
    // Hero images for current theme only (much more efficient)
    isDarkMode ? '/images/hero/big-hero-dark.jpeg' : '/images/hero/big-hero.jpeg',
    isDarkMode ? '/images/hero/small-hero-dark.jpeg' : '/images/hero/small-hero.jpeg',
    // Loading image
    '/images/loading.png',
    // First GIF for current theme only
    isDarkMode ? '/images/screenshots/landing_gif1-dark.gif' : '/images/screenshots/landing_gif1.gif'
  ];
}

/**
 * Critical images that should be preloaded on app start (minimum for landing page to work)
 */
export const CRITICAL_IMAGES = [
  // Logo images
  '/images/logos/logo-big-colored.png',
  '/images/logos/logo-big-white.png',
  '/images/logos/logo-color.png',
  '/images/logos/logo-white.png',
  // Hero images for landing page - highest priority (FIXED: .png to .jpeg)
  '/images/hero/big-hero.jpeg',
  '/images/hero/big-hero-dark.jpeg',
  '/images/hero/small-hero.jpeg',
  '/images/hero/small-hero-dark.jpeg',
  // Loading image
  '/images/loading.png',
  // First GIF (default in features section) (FIXED: .png to .gif)
  '/images/screenshots/landing_gif1.gif',
  '/images/screenshots/landing_gif1-dark.gif'
];

/**
 * Additional landing page images for instant switching
 */
export const LANDING_PAGE_IMAGES = [
  // Remaining features section GIFs (FIXED: .png to .gif)
  '/images/screenshots/landing_gif2.gif',
  '/images/screenshots/landing_gif2-dark.gif',
  '/images/screenshots/landing_gif3.gif',
  '/images/screenshots/landing_gif3-dark.gif',
  '/images/screenshots/landing_gif4.gif',
  '/images/screenshots/landing_gif4-dark.gif',
  // Canva integration image
  '/images/canva.png'
];

/**
 * All images for complete preloading
 */
export const ALL_IMAGES = [...CRITICAL_IMAGES, ...LANDING_PAGE_IMAGES];

/**
 * Preload all critical images and fonts
 * @returns {Promise<string[]>} - Promise that resolves when all critical assets are loaded
 */
export function preloadCriticalImages() {
  console.log('Starting to preload critical images and fonts...');
  
  // Start both image and font loading in parallel
  const imagePromise = preloadImages(CRITICAL_IMAGES, true);
  const fontPromise = preloadFonts();
  
  return Promise.all([imagePromise, fontPromise]).then(([images]) => {
    console.log('All critical assets preloaded:', images.length, 'images and fonts');
    return images;
  });
}

/**
 * Preload additional landing page images for instant switching
 * @param {function} onProgress - Optional progress callback
 * @returns {Promise<string[]>} - Promise that resolves when all landing images are loaded
 */
export function preloadLandingPageImages(onProgress = null) {
  console.log('Starting to preload additional landing page images...');
  
  return preloadImages(LANDING_PAGE_IMAGES, true, (loaded, total) => {
    console.log(`Additional landing page images: ${loaded}/${total} loaded`);
    if (onProgress) {
      onProgress(loaded, total);
    }
  }).then((images) => {
    console.log('Additional landing page images preloaded:', images.length, 'images');
    return images;
  });
}

/**
 * Smart preloader that loads critical images first, then continues with landing page images
 * Now uses theme-aware loading for better performance
 * @param {function} onCriticalComplete - Callback when critical images are done
 * @param {function} onProgress - Progress callback for additional images
 * @returns {Promise<{critical: string[], additional: string[]}>}
 */
export function smartPreload(onCriticalComplete = null, onProgress = null) {
  console.log('Starting smart preload strategy...');
  
  // Use theme-aware critical images for better performance
  const criticalImages = getCriticalImagesByTheme();
  
  // First, load critical images and fonts
  return Promise.all([
    preloadImages(criticalImages, true),
    preloadFonts()
  ]).then(([images]) => {
    console.log('Critical images loaded, app can start');
    
    if (onCriticalComplete) {
      onCriticalComplete(images);
    }
    
    // Then continue loading additional landing page images in the background
    return preloadLandingPageImages(onProgress).then((additionalImages) => {
      return {
        critical: images,
        additional: additionalImages
      };
    });
  });
} 